import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BellIcon } from '@heroicons/react/24/outline'
import { BellAlertIcon } from '@heroicons/react/24/solid'
import { useAuth } from '../../context/AuthContext'
import { getPowiadomienia, oznaczPrzeczytane } from '../../api/account'

/**
 * Dzwonek powiadomień w pasku, na lewo od konta (prośba właściciela 2026-10-11). Tylko dla kont firmowych:
 * powiadomienia dotyczą klientów (terminy, braki w karcie, oferty bez ruchu). Lista jest liczona przez backend
 * przy odczycie (`accounts/powiadomienia.py`), więc znika sama, gdy sprawa zostanie załatwiona. Odświeżamy ją
 * po wejściu, co 5 minut i po powrocie do karty przeglądarki. Klik w powiadomienie oznacza je jako przeczytane
 * i otwiera kartę klienta.
 * `przypisanie` (2026-10-11): „Przypisano Ci klienta: …”, tylko dla osoby, której ktoś przypisał klienta
 * (zmiana osoby prowadzącej albo przepięcie przy usuwaniu kogoś z zespołu). Przeczytane gaśnie jak inne,
 * a z listy schodzi po 14 dniach albo gdy klienta przypisano dalej.
 * Rynek (2026-10-30): `rynek_nowe`, `rynek_koniec` i `rynek_wybrana` mają `adres` (zlecenie) zamiast `klient_id`. Decyzje na Rynku
 * wysyłają `vv-rynek-odswiez` (`api/rynek`), więc pozycja gaśnie od razu; ten sam sygnał obsługuje licznik na ikonie.
 */

const CO_MS = 5 * 60 * 1000
const ZNACZNIK = { montaz_braki: 'bg-amber-500', montaz_odbyl: 'bg-red-500', spotkanie_odbylo: 'bg-red-400',
  spotkanie_wkrotce: 'bg-blue-500', klient_bez_kontaktu: 'bg-gray-400', przypisanie: 'bg-emerald-500',
  rynek_nowe: 'bg-blue-500', rynek_koniec: 'bg-red-500', rynek_wybrana: 'bg-emerald-500' }

const Dzwonek = () => {
  const { isAuthenticated, firmowe } = useAuth()
  const navigate = useNavigate()
  const [dane, setDane] = useState({ nieprzeczytane: 0, powiadomienia: [] })
  const [otwarte, setOtwarte] = useState(false)
  const ref = useRef(null)
  const aktywne = isAuthenticated && firmowe

  const odswiez = useCallback(async () => {
    const res = await getPowiadomienia()
    if (res.ok) setDane(res.data)
  }, [])

  useEffect(() => {
    if (!aktywne) return undefined
    odswiez()
    const co = setInterval(odswiez, CO_MS)
    const naFokus = () => { if (document.visibilityState === 'visible') odswiez() }
    document.addEventListener('visibilitychange', naFokus)
    window.addEventListener('vv-powiadomienia-odswiez', odswiez)
    window.addEventListener('vv-rynek-odswiez', odswiez)
    return () => {
      clearInterval(co)
      document.removeEventListener('visibilitychange', naFokus)
      window.removeEventListener('vv-powiadomienia-odswiez', odswiez)
      window.removeEventListener('vv-rynek-odswiez', odswiez)
    }
  }, [aktywne, odswiez])

  useEffect(() => {
    if (!otwarte) return undefined
    const poza = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarte(false) }
    const esc = (e) => { if (e.key === 'Escape') setOtwarte(false) }
    document.addEventListener('mousedown', poza)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', poza); document.removeEventListener('keydown', esc) }
  }, [otwarte])

  if (!aktywne) return null

  const { nieprzeczytane, powiadomienia } = dane
  const otworz = async (p) => {
    setOtwarte(false)
    if (!p.przeczytane) {
      setDane((d) => ({
        nieprzeczytane: Math.max(0, d.nieprzeczytane - 1),
        powiadomienia: d.powiadomienia.map((x) => (x.klucz === p.klucz ? { ...x, przeczytane: true } : x)),
      }))
      oznaczPrzeczytane({ klucze: [p.klucz] })
    }
    navigate(p.adres || `/klienci/${p.klient_id}`)
  }
  const wszystkie = async () => {
    setDane((d) => ({ nieprzeczytane: 0, powiadomienia: d.powiadomienia.map((x) => ({ ...x, przeczytane: true })) }))
    await oznaczPrzeczytane({ wszystkie: true })
  }

  return (
    // `flex items-center`: wrapper rozciąga się na wysokość paska, a sam przycisk ma 36 px, więc bez
    // wyśrodkowania dzwonek wisiał 4 px nad awatarem konta.
    <div className="relative flex items-center" ref={ref}>
      <button type="button" onClick={() => setOtwarte((o) => !o)} aria-haspopup="dialog" aria-expanded={otwarte}
              aria-label={nieprzeczytane ? `Powiadomienia: ${nieprzeczytane} nieprzeczytanych` : 'Powiadomienia'}
              title="Powiadomienia"
              className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                otwarte ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'}`}>
        {nieprzeczytane ? <BellAlertIcon className="h-5 w-5 text-blue-600" /> : <BellIcon className="h-5 w-5" />}
        {nieprzeczytane > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white">
            {nieprzeczytane > 9 ? '9+' : nieprzeczytane}
          </span>
        )}
      </button>

      {otwarte && (
        <div role="dialog" aria-label="Powiadomienia"
             className="absolute right-0 top-full z-50 mt-2 w-[22rem] max-w-[calc(100vw-1.5rem)] rounded-2xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Powiadomienia</p>
            {nieprzeczytane > 0 && (
              <button type="button" onClick={wszystkie} className="text-xs font-semibold text-blue-700 hover:underline">
                Oznacz wszystkie jako przeczytane
              </button>
            )}
          </div>
          {powiadomienia.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-500">Nic na teraz. Wszystko pod kontrolą.</p>
          ) : (
            <ul className="max-h-[26rem] divide-y divide-gray-100 overflow-y-auto">
              {powiadomienia.map((p) => (
                <li key={p.klucz}>
                  <button type="button" onClick={() => otworz(p)}
                          className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 ${p.przeczytane ? '' : 'bg-blue-50/40'}`}>
                    <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${p.przeczytane ? 'bg-gray-200' : (ZNACZNIK[p.typ] || 'bg-blue-500')}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm leading-5 ${p.przeczytane ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>{p.tytul}</span>
                      <span className="mt-0.5 block text-xs leading-5 text-gray-500">{p.tresc}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-gray-100 px-4 py-2.5 text-right">
            <Link to="/konto?sekcja=powiadomienia" onClick={() => setOtwarte(false)}
                  className="text-xs font-semibold text-gray-600 no-underline hover:text-blue-700">Ustawienia powiadomień</Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dzwonek
